import type { ComponentProps, ReactNode } from 'react'

import {
  CustomNameSelector,
  DefaultPlaceholderInserterPair,
  FanNameSelector,
} from '@/modules/message/components/editor/fields/default-placeholder-inserter-pair'
import { MessageFallbackSelectorController } from '@/modules/message/components/editor/fields/message-fallback-selector-controller'
import { MessageLabelInserter } from '@/modules/message/components/editor/fields/message-label-inserter'
import { MessageMediaSelectorController } from '@/modules/message/components/editor/fields/message-media-selector-controller'
import { MessageTextSelector } from '@/modules/message/components/editor/fields/message-text-selector'
import { MessageTitleSelectorController } from '@/modules/message/components/editor/fields/message-title-selector-controller'
import { useMessageEditor } from '@/modules/message/components/editor/message-editor-context'
import { classNames } from '@/utils/helpers'

export type MessageEditorProps = ComponentProps<'div'>

export const MessageEditor = (props: MessageEditorProps) => {
  const { components } = useMessageEditor()

  return (
    <MessageEditorContent {...props}>
      {components.includes('title') && (
        <MessageTitleSelectorController className="col-span-12" />
      )}
      {components.includes('text') && (
        <MessageTextSelector className="col-span-12" />
      )}
      {components.includes('customAndFanNameLabels') && (
        <DefaultPlaceholderInserterPair className="col-span-12" />
      )}
      {components.includes('customName') && (
        <MessageLabelInserter className="col-span-12">
          {({ insert }) => <CustomNameSelector insert={insert} />}
        </MessageLabelInserter>
      )}
      {components.includes('fanName') && (
        <MessageLabelInserter className="col-span-12">
          {({ insert }) => <FanNameSelector insert={insert} />}
        </MessageLabelInserter>
      )}
      {components.includes('fallback') && (
        <MessageFallbackSelectorController className="col-span-12" />
      )}
      {components.includes('media') && (
        <MessageMediaSelectorController className="col-span-12" />
      )}
    </MessageEditorContent>
  )
}

interface ChildRenderProps {
  editor: ReturnType<typeof useMessageEditor>
}

type MessageEditorContentProps = Omit<ComponentProps<'div'>, 'children'> & {
  children: ReactNode | (({ editor }: ChildRenderProps) => ReactNode)
}

const MessageEditorContent = (props: MessageEditorContentProps) => {
  const { className, children, ...rest } = props
  const editor = useMessageEditor()

  return (
    <div
      {...rest}
      className={classNames(
        'mt-[24px] grid grid-cols-12 gap-[24px]',
        className,
      )}
    >
      {typeof children === 'function' ? children({ editor }) : children}
    </div>
  )
}

MessageEditor.Content = MessageEditorContent
